import { useMutation } from '@apollo/client'
import LicenceInput from 'components/me/subscriptionManagement/Licences/LicenceInput/LicenceInput'
import { ModalContext } from 'context/Modal'
import { logError } from 'logService'
import PropTypes from 'prop-types'
import { useContext, useState } from 'react'
import { toast } from 'react-toastify'
import { isEmail } from 'utils/regex'

import {
  ADD_TEAM_MEMBER,
  DELETE_TEAM_MEMBER,
} from '@/graphql/mutations/user'
import { LICENCES_STATUS } from '@/graphql/queries/user'

const Container = ({
  lastname,
  firstname,
  email,
  ownerEmail,
  ...props
}) => {
  const [addTeamMember, { loading: addTeamMemberLoading }] = useMutation(ADD_TEAM_MEMBER)
  const [deleteTeamMember] = useMutation(DELETE_TEAM_MEMBER)

  const [sent, setSent] = useState(email.length !== 0)
  const [form, setForm] = useState({
    lastname,
    firstname,
    email,
  })
  const [errors, setErrors] = useState({})

  const { actions } = useContext(ModalContext)
  const { closeModal, setModal } = actions

  const clearForm = () => {
    setForm({
      lastname: '',
      firstname: '',
      email: '',
    })
    setSent(false)
  }

  const handleChange = (value, name) => {
    let updatedErrors = { ...errors }

    updateFormValue(value, name)

    setErrors({
      ...updatedErrors,
      [name]: null,
    })
  }

  const updateFormValue = (value, name) => {
    setForm({
      ...form,
      [name]: value,
    })
  }

  const handleSubmit = async e => {
    e.preventDefault()

    if (sent) {
      return
    }

    const updatedErrors = {}

    if (!isEmail.test(form.email)) {
      updatedErrors.email = 'Champ incorrect'
    }
    if (form.firstname.length === 0) {
      updatedErrors.firstname = 'Champ obligatoire'
    }
    if (form.lastname.length === 0) {
      updatedErrors.lastname = 'Champ obligatoire'
    }

    setErrors(updatedErrors)
    const hasError = Object.values(updatedErrors).some(error => typeof error === 'string')

    if (!hasError) {
      try {
        const { data } = await addTeamMember({
          variables: {
            ...form,
          },
        })

        if (data.addTeamMember.id !== undefined) {
          setSent(true)
        }
      } catch (error) {
        let message = ''
        if (e.networkError.result.errors[0].message === 'This email is already used.') {
          message = `Ce compte existe déjà ! Contactez le Service Client pour mettre en place l'abonnement : ${import.meta.env.VITE_CONTACT_ADDRESS}`
        } else {
          message = 'Oups, une erreur est survenue'
        }
        toast.error(message, {
          position: 'top-center',
        })
        logError('GRAPHQL', 'addTeamMember', error)
      }
    }
  }

  const handleDeleteClick = async () => {
    setModal({
      title: 'Supprimer un bénéficiare',
      details: `Si vous supprimez ce bénéficiaire, il cessera d'avoir accès aux Quiz Défimédoc immédiatemment.
                Vous pourrez le remplacer par un autre bénéficiaire, qui aura immédiatemment accès aux Quiz publiés.
                `,
      cancel: {
        text: 'Annuler',
        handleClick: closeModal,
      },
      validate: {
        text: 'Supprimer le bénéficiaire',
        handleClick: handleDelete,
      },
    })
  }

  const handleDelete = async e => {
    e.preventDefault()

    try {
      await deleteTeamMember({
        variables: {
          email: form.email,
        },
        refetchQueries: [{
          query: LICENCES_STATUS,
        }],
      })
      clearForm()
      toast.success('Bénéficiaire supprimé. Vous pouvez dès à présent en ajouter un nouveau.', {
        position: 'top-center',
      })
    } catch (error) {
      toast.error('Oups, une erreur est survenue', {
        position: 'top-center',
      })
      logError('GRAPHQL', 'deleteTeamMember', error)
    }
    closeModal()
  }

  // The owner cannot be deleted from the subscription team members
  const disableDelete = ownerEmail === email

  return (
    <LicenceInput
      disableDelete={ disableDelete }
      errors={ errors }
      form={ form }
      loading={ addTeamMemberLoading }
      sent={ sent }
      onChange={ handleChange }
      onDelete={ handleDeleteClick }
      onSubmit={ handleSubmit }
      { ...props }
    />
  )
}

Container.propTypes = {
  email: PropTypes.string.isRequired,
  firstname: PropTypes.string.isRequired,
  lastname: PropTypes.string.isRequired,
  ownerEmail: PropTypes.string.isRequired,
}

export default Container
